import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';

import { useProject } from '@contexts/Project.context';
import { getMasterFeatures } from '@/utils/api';
import { createFeature, noOverlapOffset, deleteLayerByCustomId } from '@utils/map/helpers';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';

// Local Style Constants
const STROKE_WIDTH = 4;

/** Local Style For AOI Layer */
const STYLE = {
	'stroke-color': 'rgba(255, 255, 0, 0.8)',
	'stroke-width': STROKE_WIDTH,
	'stroke-offset': noOverlapOffset(STROKE_WIDTH),
	'stroke-line-dash': [10, 10],
};

/* This component is responsible for rendering the area of interest layer and toggling its visibility. */
const AoiLayer = ({ layerId = 'aoiLayer' }) => {
	const { mapObject, project, pickedTask, defaultFormat } = useProject();

	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const [layer, setLayer] = useState(null);
	const { data, error } = useQuery({
		queryKey: ['fetchAOI', projectUuid, modelUuid],
		queryFn: () => getMasterFeatures(projectUuid, modelUuid, 'detected_areas'),
		enabled: !!projectUuid && !!modelUuid,
		refetchInterval: 60_000 * 5, // 5min
		refetchIntervalInBackground: true,
	});

	const { features: rawFeatures = [] } = data ?? {};

	// Creates the AOI Layer
	useEffect(() => {
		const layerInstance = new WebGLVectorLayer({
			name: 'Model boundary',
			zIndex: 11,
			visible: true,
			source: new VectorSource({}),
			properties: { customLayerId: layerId },
			style: {},
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);
		return () => {
			deleteLayerByCustomId(mapObject, layerId);
			setLayer(null);
		};
	}, []);

	useEffect(() => {
		if (error) return console.error('Error fetching aoi layer: ', error);
		if (!layer || !rawFeatures) return;

		const features = rawFeatures.reduce((acc, { geometry, properties }) => {
			const feature = createFeature(geometry, properties);
			if (feature) acc.push(feature);
			return acc;
		}, []);

		layer.setStyle(STYLE);
		layer.setSource(new VectorSource({ features, format: defaultFormat }));
	}, [layer, data, error]);

	if (!layer || !data) return null;
	return (
		<li>
			<LayerSettings
				id={layerId}
				layer={layer}
				label={layer?.get('name')}
				defaultState={layer?.getVisible()}
				onCheck={() => layer?.setVisible(true)}
				onUncheck={() => layer?.setVisible(false)}
			/>
		</li>
	);
};
export default AoiLayer;
